import React, { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Input, Select, Checkbox, Button, Modal, Spin, ConfigProvider } from 'antd';
import { AddressSuggestions, PartySuggestions, FioSuggestions } from 'react-dadata';
import 'react-dadata/dist/react-dadata.css';
import lowerCase from 'lodash/lowerCase';

import PhoneCountryMask from '../../../features/address/PhoneCountryMask';
import {
    PROFILE_FIELD_NAMES,
    profileActions,
    profileSelectors,
    saveProfile,
    changePassword,
} from '../../../features/profile';
import { addressSelectors, fetchCountries } from '../../../features/address';
import { EAEU_COUNTRIES, getRegionsForCountry } from '../../../features/geolocation';
import { DADATA_API_KEY } from '../../../config/secrets';

const { Option } = Select;

const ProfilePersonalData = () => {
    const dispatch = useDispatch();

    const fields = useSelector(profileSelectors.selectProfileFields);
    const errors = useSelector(profileSelectors.selectFormErrors, shallowEqual);
    const isSaving = useSelector(profileSelectors.selectIsSaving);
    const saveSuccess = useSelector(profileSelectors.selectSaveSuccess);
    const hasChanges = useSelector(profileSelectors.selectHasChanges);
    const isChangingPassword = useSelector(profileSelectors.selectIsChangingPassword);

    const isFetchingCountries = useSelector(addressSelectors.selectIsCountriesFetching);
    const countries = useSelector(addressSelectors.selectCountries);

    const [isJuridic, setIsJuridic] = useState(fields[PROFILE_FIELD_NAMES.IS_JURIDIC] || false);
    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState({});

    useEffect(() => {
        if (!countries || (Array.isArray(countries) && countries.length === 0)) {
            dispatch(fetchCountries());
        }
    }, [dispatch]);

    useEffect(() => {
        setIsJuridic(fields[PROFILE_FIELD_NAMES.IS_JURIDIC] || false);
    }, [fields[PROFILE_FIELD_NAMES.IS_JURIDIC]]);

    let listPhoneCountries = [];
    if (!isFetchingCountries && countries && countries.countries) {
        listPhoneCountries = Object.values(countries.countries).map((element, index) => ({
            key: index,
            text: element.country_ru,
            flag: lowerCase(element.iso),
            value: element.iso,
            className: "dropdown-item",
            phone_mask: element.phone_mask,
        }));
    }

    const selectedCountry = fields[PROFILE_FIELD_NAMES.COUNTRY] || 'RU';
    const regions = getRegionsForCountry(selectedCountry);

    const handleFieldChange = useCallback((fieldName, value) => {
        dispatch(profileActions.setProfileField(fieldName, value));
    }, [dispatch]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        handleFieldChange(name, value);
    }, [handleFieldChange]);

    const handleSelectChange = useCallback((fieldName) => (value) => {
        handleFieldChange(fieldName, value);
    }, [handleFieldChange]);

    const handleCountryChange = useCallback((value) => {
        dispatch(profileActions.setProfileFields({
            [PROFILE_FIELD_NAMES.COUNTRY]: value,
            [PROFILE_FIELD_NAMES.REGION]: '',
        }));
    }, [dispatch]);

    const handleJuridicChange = useCallback((e) => {
        const checked = e.target.checked;
        setIsJuridic(checked);
        handleFieldChange(PROFILE_FIELD_NAMES.IS_JURIDIC, checked);
    }, [handleFieldChange]);

    const handleCompanySelect = useCallback((suggestion) => {
        if (!suggestion) return;

        dispatch(profileActions.setProfileFields({
            [PROFILE_FIELD_NAMES.COMPANY]: suggestion.value,
            [PROFILE_FIELD_NAMES.COMPANY_INN]: suggestion.data.inn || '',
            [PROFILE_FIELD_NAMES.COMPANY_KPP]: suggestion.data.kpp || '',
            [PROFILE_FIELD_NAMES.COMPANY_OGRN]: suggestion.data.ogrn || '',
            [PROFILE_FIELD_NAMES.COMPANY_CHIEF_NAME]: suggestion.data.management?.name || '',
            [PROFILE_FIELD_NAMES.COMPANY_LEGAL_ADDR]: suggestion.data.address?.unrestricted_value || '',
        }));
    }, [dispatch]);

    const handleAddressSelect = useCallback((fieldName) => (suggestion) => {
        if (suggestion) {
            handleFieldChange(fieldName, suggestion.unrestricted_value || suggestion.value);
        }
    }, [handleFieldChange]);

    const handleFioSelect = useCallback((fieldName) => (suggestion) => {
        if (suggestion) {
            handleFieldChange(fieldName, suggestion.value);
        }
    }, [handleFieldChange]);

    const handleSave = useCallback(() => {
        dispatch(saveProfile());
    }, [dispatch]);

    const handleReset = useCallback(() => {
        dispatch(profileActions.resetProfileFields());
    }, [dispatch]);

    const handlePasswordSubmit = useCallback(() => {
        const newErrors = {};

        if (!passwordForm.currentPassword) {
            newErrors.currentPassword = 'Введите текущий пароль';
        }
        if (!passwordForm.newPassword) {
            newErrors.newPassword = 'Введите новый пароль';
        } else if (passwordForm.newPassword.length < 8) {
            newErrors.newPassword = 'Пароль должен быть не менее 8 символов';
        } else if (!/[a-zA-Z]/.test(passwordForm.newPassword)) {
            newErrors.newPassword = 'Пароль должен содержать латинские буквы';
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            newErrors.confirmPassword = 'Пароли не совпадают';
        }

        if (Object.keys(newErrors).length > 0) {
            setPasswordErrors(newErrors);
            return;
        }

        dispatch(changePassword(passwordForm.currentPassword, passwordForm.newPassword))
            .then(() => {
                setPasswordModalVisible(false);
                setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                setPasswordErrors({});
            });
    }, [dispatch, passwordForm]);

    const antTheme = {
        token: {
            colorPrimary: '#D00202',
            colorBgContainer: 'transparent',
            colorText: '#fff',
            colorTextPlaceholder: 'rgba(255, 255, 255, 0.4)',
            colorBorder: 'rgba(255, 255, 255, 0.3)',
        },
    };

    return (
        <ConfigProvider theme={antTheme}>
            <div className="profile-form-section">

                <div className="profile-form-header">
                    <h2>Личные данные</h2>
                    <Button
                        className="change-password-btn"
                        onClick={() => setPasswordModalVisible(true)}
                    >
                        Изменить пароль
                    </Button>
                </div>

                <div className="profile-form-grid">

                    <div className="profile-form-row">
                        <div className="profile-form-field">
                            <label>Ваш логин</label>
                            <Input
                                className="profile-input"
                                name={PROFILE_FIELD_NAMES.LOGIN}
                                value={fields[PROFILE_FIELD_NAMES.LOGIN]}
                                onChange={handleInputChange}
                                placeholder="Логин"
                                disabled
                            />
                        </div>
                    </div>

                    <div className="profile-form-row">
                        <div className="profile-form-field">
                            <label>Страна <span className="required">*</span></label>
                            <Select
                                className="profile-select"
                                value={fields[PROFILE_FIELD_NAMES.COUNTRY]}
                                onChange={handleCountryChange}
                                placeholder="Выберите страну"
                                popupClassName="profile-select-dropdown"
                            >
                                {EAEU_COUNTRIES.map(c => (
                                    <Option key={c.code} value={c.code}>{c.name}</Option>
                                ))}
                            </Select>
                        </div>
                        <div className="profile-form-field">
                            <label>Регион <span className="required">*</span></label>
                            <Select
                                className="profile-select"
                                value={fields[PROFILE_FIELD_NAMES.REGION] || undefined}
                                onChange={handleSelectChange(PROFILE_FIELD_NAMES.REGION)}
                                placeholder="Выберите регион"
                                showSearch
                                optionFilterProp="children"
                                popupClassName="profile-select-dropdown"
                            >
                                {regions.map(region => (
                                    <Option key={region.code} value={region.code}>{region.name}</Option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    <div className="profile-form-row">
                        <div className="profile-form-field">
                            <label>Населенный пункт</label>
                            <Input
                                className="profile-input"
                                name={PROFILE_FIELD_NAMES.CITY}
                                value={fields[PROFILE_FIELD_NAMES.CITY]}
                                onChange={handleInputChange}
                                placeholder="Населенный пункт"
                            />
                        </div>
                        <div className="profile-form-field">
                            <label>Индекс</label>
                            <Input
                                className="profile-input"
                                name={PROFILE_FIELD_NAMES.POSTAL_CODE}
                                value={fields[PROFILE_FIELD_NAMES.POSTAL_CODE]}
                                onChange={handleInputChange}
                                placeholder="Индекс для доставки"
                            />
                        </div>
                    </div>

                    <div className="profile-form-row">
                        <div className="profile-form-field">
                            <label>Имя <span className="required">*</span></label>
                            <Input
                                className="profile-input"
                                name={PROFILE_FIELD_NAMES.FIRSTNAME}
                                value={fields[PROFILE_FIELD_NAMES.FIRSTNAME]}
                                onChange={handleInputChange}
                                placeholder="Имя"
                            />
                            {errors[PROFILE_FIELD_NAMES.FIRSTNAME] && (
                                <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.FIRSTNAME]}</span>
                            )}
                        </div>
                        <div className="profile-form-field">
                            <label>Фамилия <span className="required">*</span></label>
                            <Input
                                className="profile-input"
                                name={PROFILE_FIELD_NAMES.LASTNAME}
                                value={fields[PROFILE_FIELD_NAMES.LASTNAME]}
                                onChange={handleInputChange}
                                placeholder="Фамилия"
                            />
                            {errors[PROFILE_FIELD_NAMES.LASTNAME] && (
                                <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.LASTNAME]}</span>
                            )}
                        </div>
                    </div>

                    <div className="profile-form-row">
                        <div className="profile-form-field">
                            <label>Телефон <span className="required">*</span></label>
                            <PhoneCountryMask
                                countries={listPhoneCountries}
                                loading={isFetchingCountries}
                                country={fields[PROFILE_FIELD_NAMES.PHONE_COUNTRY]}
                                phone={fields[PROFILE_FIELD_NAMES.PHONE]}
                                handleChangeField={handleFieldChange}
                                nameCountry={PROFILE_FIELD_NAMES.PHONE_COUNTRY}
                                namePhone={PROFILE_FIELD_NAMES.PHONE}
                                extraClass="profile-phone-wrapper"
                                fluid
                            />
                            {errors[PROFILE_FIELD_NAMES.PHONE] && (
                                <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.PHONE]}</span>
                            )}
                        </div>
                        <div className="profile-form-field">
                            <label>Электронная почта <span className="required">*</span></label>
                            <Input
                                className="profile-input"
                                name={PROFILE_FIELD_NAMES.EMAIL}
                                value={fields[PROFILE_FIELD_NAMES.EMAIL]}
                                onChange={handleInputChange}
                                placeholder="Электронная почта"
                                type="email"
                            />
                            {errors[PROFILE_FIELD_NAMES.EMAIL] && (
                                <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.EMAIL]}</span>
                            )}
                        </div>
                    </div>

                    <div className="profile-juridic-checkbox">
                        <Checkbox
                            checked={isJuridic}
                            onChange={handleJuridicChange}
                        >
                            Я являюсь юридическим лицом
                        </Checkbox>
                    </div>

                    {isJuridic && (
                        <div className="profile-company-section">
                            <h3>Данные компании</h3>

                            <div className="profile-form-row single-column">
                                <div className="profile-form-field full-width">
                                    <label>Название компании или ИП <span className="required">*</span></label>
                                    <PartySuggestions
                                        containerClassName="profile-dadata-wrapper"
                                        inputProps={{
                                            placeholder: 'Название компании или ИП',
                                            name: PROFILE_FIELD_NAMES.COMPANY,
                                            value: fields[PROFILE_FIELD_NAMES.COMPANY] || '',
                                            onChange: handleInputChange,
                                        }}
                                        defaultQuery={fields[PROFILE_FIELD_NAMES.COMPANY]}
                                        hintText="Выберите организацию..."
                                        count={5}
                                        token={DADATA_API_KEY}
                                        onChange={handleCompanySelect}
                                    />
                                    {errors[PROFILE_FIELD_NAMES.COMPANY] && (
                                        <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.COMPANY]}</span>
                                    )}
                                </div>
                            </div>

                            <div className="profile-form-row three-columns">
                                <div className="profile-form-field">
                                    <label>ОГРН</label>
                                    <Input
                                        className="profile-input"
                                        name={PROFILE_FIELD_NAMES.COMPANY_OGRN}
                                        value={fields[PROFILE_FIELD_NAMES.COMPANY_OGRN]}
                                        onChange={handleInputChange}
                                        placeholder="ОГРН"
                                        disabled={!!fields[PROFILE_FIELD_NAMES.COMPANY_OGRN]}
                                    />
                                    {errors[PROFILE_FIELD_NAMES.COMPANY_OGRN] && (
                                        <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.COMPANY_OGRN]}</span>
                                    )}
                                </div>
                                <div className="profile-form-field">
                                    <label>ИНН</label>
                                    <Input
                                        className="profile-input"
                                        name={PROFILE_FIELD_NAMES.COMPANY_INN}
                                        value={fields[PROFILE_FIELD_NAMES.COMPANY_INN]}
                                        onChange={handleInputChange}
                                        placeholder="ИНН"
                                        disabled={!!fields[PROFILE_FIELD_NAMES.COMPANY_INN]}
                                    />
                                    {errors[PROFILE_FIELD_NAMES.COMPANY_INN] && (
                                        <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.COMPANY_INN]}</span>
                                    )}
                                </div>
                                <div className="profile-form-field">
                                    <label>КПП</label>
                                    <Input
                                        className="profile-input"
                                        name={PROFILE_FIELD_NAMES.COMPANY_KPP}
                                        value={fields[PROFILE_FIELD_NAMES.COMPANY_KPP]}
                                        onChange={handleInputChange}
                                        placeholder="КПП"
                                        disabled={!!fields[PROFILE_FIELD_NAMES.COMPANY_KPP]}
                                    />
                                    {errors[PROFILE_FIELD_NAMES.COMPANY_KPP] && (
                                        <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.COMPANY_KPP]}</span>
                                    )}
                                </div>
                            </div>

                            <div className="profile-form-row">
                                <div className="profile-form-field">
                                    <label>Руководитель</label>
                                    <Input
                                        className="profile-input"
                                        name={PROFILE_FIELD_NAMES.COMPANY_CHIEF_NAME}
                                        value={fields[PROFILE_FIELD_NAMES.COMPANY_CHIEF_NAME]}
                                        onChange={handleInputChange}
                                        placeholder="Руководитель"
                                        disabled={!!fields[PROFILE_FIELD_NAMES.COMPANY_CHIEF_NAME]}
                                    />
                                </div>
                                <div className="profile-form-field">
                                    <label>ФИО</label>
                                    <FioSuggestions
                                        containerClassName="profile-dadata-wrapper"
                                        inputProps={{
                                            placeholder: 'ФИО',
                                            name: PROFILE_FIELD_NAMES.COMPANY_CONTACT_NAME,
                                            value: fields[PROFILE_FIELD_NAMES.COMPANY_CONTACT_NAME] || '',
                                            onChange: handleInputChange,
                                        }}
                                        defaultQuery={fields[PROFILE_FIELD_NAMES.COMPANY_CONTACT_NAME]}
                                        hintText="Выберите вариант..."
                                        filterParts={['NAME', 'SURNAME', 'PATRONYMIC']}
                                        count={4}
                                        token={DADATA_API_KEY}
                                        onChange={handleFioSelect(PROFILE_FIELD_NAMES.COMPANY_CONTACT_NAME)}
                                    />
                                </div>
                            </div>

                            <div className="profile-form-row single-column">
                                <div className="profile-form-field full-width">
                                    <label>Юридический адрес</label>
                                    <AddressSuggestions
                                        containerClassName="profile-dadata-wrapper"
                                        inputProps={{
                                            placeholder: 'Юридический адрес',
                                            name: PROFILE_FIELD_NAMES.COMPANY_LEGAL_ADDR,
                                            value: fields[PROFILE_FIELD_NAMES.COMPANY_LEGAL_ADDR] || '',
                                            onChange: handleInputChange,
                                        }}
                                        defaultQuery={fields[PROFILE_FIELD_NAMES.COMPANY_LEGAL_ADDR]}
                                        hintText="Выберите вариант..."
                                        count={4}
                                        token={DADATA_API_KEY}
                                        onChange={handleAddressSelect(PROFILE_FIELD_NAMES.COMPANY_LEGAL_ADDR)}
                                        filterFromBound="city"
                                        filterToBound="house"
                                    />
                                </div>
                            </div>

                            <div className="profile-form-row single-column">
                                <div className="profile-form-field full-width">
                                    <label>Почтовый адрес</label>
                                    <AddressSuggestions
                                        containerClassName="profile-dadata-wrapper"
                                        inputProps={{
                                            placeholder: 'Почтовый адрес',
                                            name: PROFILE_FIELD_NAMES.COMPANY_POST_ADDR,
                                            value: fields[PROFILE_FIELD_NAMES.COMPANY_POST_ADDR] || '',
                                            onChange: handleInputChange,
                                        }}
                                        defaultQuery={fields[PROFILE_FIELD_NAMES.COMPANY_POST_ADDR]}
                                        hintText="Выберите вариант..."
                                        count={4}
                                        token={DADATA_API_KEY}
                                        onChange={handleAddressSelect(PROFILE_FIELD_NAMES.COMPANY_POST_ADDR)}
                                        filterFromBound="city"
                                        filterToBound="house"
                                    />
                                </div>
                            </div>

                            <div className="profile-form-row single-column">
                                <div className="profile-form-field full-width">
                                    <label>Сфера деятельности</label>
                                    <Input
                                        className="profile-input"
                                        name={PROFILE_FIELD_NAMES.COMPANY_SCOPE}
                                        value={fields[PROFILE_FIELD_NAMES.COMPANY_SCOPE]}
                                        onChange={handleInputChange}
                                        placeholder="Сфера деятельности"
                                    />
                                </div>
                            </div>

                            <div className="profile-form-row single-column">
                                <div className="profile-form-field full-width">
                                    <label>Кем являетесь / должность <span className="required">*</span></label>
                                    <Input
                                        className="profile-input"
                                        name={PROFILE_FIELD_NAMES.COMPANY_JOB_TITLE}
                                        value={fields[PROFILE_FIELD_NAMES.COMPANY_JOB_TITLE]}
                                        onChange={handleInputChange}
                                        placeholder="Кого представляете или должность в компании"
                                    />
                                    {errors[PROFILE_FIELD_NAMES.COMPANY_JOB_TITLE] && (
                                        <span className="profile-field-error">{errors[PROFILE_FIELD_NAMES.COMPANY_JOB_TITLE]}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="profile-save-section">
                        <Button
                            className="profile-reset-btn"
                            onClick={handleReset}
                            disabled={!hasChanges || isSaving}
                        >
                            Отменить изменения
                        </Button>
                        <Button
                            className="profile-save-btn"
                            onClick={handleSave}
                            loading={isSaving}
                            disabled={Object.keys(errors).length > 0}
                        >
                            {isSaving ? 'Сохранение...' : 'Сохранить изменения'}
                        </Button>
                    </div>
                </div>

                <Modal
                    title="Изменить пароль"
                    open={passwordModalVisible}
                    onCancel={() => {
                        setPasswordModalVisible(false);
                        setPasswordErrors({});
                    }}
                    footer={[
                        <Button
                            key="cancel"
                            onClick={() => setPasswordModalVisible(false)}
                        >
                            Отмена
                        </Button>,
                        <Button
                            key="submit"
                            className="password-submit-btn"
                            onClick={handlePasswordSubmit}
                            loading={isChangingPassword}
                        >
                            Изменить пароль
                        </Button>,
                    ]}
                    className="password-modal"
                    width={440}
                >
                    <div className="password-form-field">
                        <label>Текущий пароль</label>
                        <Input.Password
                            className="password-input"
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                            placeholder="Введите текущий пароль"
                        />
                        {passwordErrors.currentPassword && (
                            <span className="profile-field-error">{passwordErrors.currentPassword}</span>
                        )}
                    </div>
                    <div className="password-form-field">
                        <label>Новый пароль</label>
                        <Input.Password
                            className="password-input"
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                            placeholder="Введите новый пароль"
                        />
                        {passwordErrors.newPassword && (
                            <span className="profile-field-error">{passwordErrors.newPassword}</span>
                        )}
                    </div>
                    <div className="password-form-field">
                        <label>Подтвердите пароль</label>
                        <Input.Password
                            className="password-input"
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                            placeholder="Повторите новый пароль"
                        />
                        {passwordErrors.confirmPassword && (
                            <span className="profile-field-error">{passwordErrors.confirmPassword}</span>
                        )}
                    </div>
                </Modal>
            </div>
        </ConfigProvider>
    );
};

export default ProfilePersonalData;
